import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { useSearchParams } from "react-router-dom";
import { ReportService } from "../../../services/ReportService";
import PrintButton from "../../../components/buttons/PrintButton";

export default function ReportFechamentoSintetico() {
  const [searchParams] = useSearchParams();
  const selectedPeriod = searchParams.get("period");

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reportService = new ReportService();

  const periodParts = selectedPeriod ? selectedPeriod.split("-") : [];
  const [month, year] = periodParts.map(Number);

  const isValidPeriod =
    periodParts.length === 2 &&
    !isNaN(month) &&
    !isNaN(year) &&
    month >= 1 &&
    month <= 12;

  useEffect(() => {
    if (!isValidPeriod) return;

    setLoading(true);
    setError("");

    reportService
      .getFechamentoSinteticoReport(selectedPeriod)
      .then((data) => setReportData(data || []))
      .catch(() => setError("Erro ao carregar o relatório."))
      .finally(() => setLoading(false));
  }, [selectedPeriod, isValidPeriod]);

  if (!selectedPeriod) {
    return (
      <div className="text-danger mt-3">Erro: Período não especificado.</div>
    );
  }

  if (!isValidPeriod) {
    return (
      <div className="text-danger mt-3">Erro: Formato do período inválido.</div>
    );
  }

  const periodDate = DateTime.fromObject({ year, month }, { zone: "America/Sao_Paulo" });

  const monthName = periodDate
    .setLocale("pt-BR")
    .toFormat("MMMM")
    .toUpperCase();
  const startDate = periodDate.startOf("month").toFormat("dd/MM/yyyy");
  const endDate = periodDate.endOf("month").toFormat("dd/MM/yyyy");

  const formatDateTime = (reservation) => {
    const startDate = DateTime.fromISO(reservation.startHour, {
      zone: "UTC",
    }).setZone("America/Sao_Paulo");
    const endDate = DateTime.fromISO(reservation.endHour, {
      zone: "UTC",
    }).setZone("America/Sao_Paulo");

    const weekday = startDate.setLocale("pt-BR").toFormat("ccc").toUpperCase();
    const formattedDate = startDate.toFormat("dd/MM/yyyy");
    const formattedStartHour = startDate.toFormat("HH:mm");
    const formattedEndHour = endDate.toFormat("HH:mm");

    return `${weekday.replace(
      /.$/,
      ""
    )} ${formattedDate} ${formattedStartHour} ÀS ${formattedEndHour}`;
  };

  const yearAndMonth = periodDate.toFormat("yyyy-MM");

  // Group data by user
  const groupedByUser = reportData.reduce((acc, user) => {
    if (!acc[user.username]) {
      acc[user.username] = {
        ...user,
        reservations: [],
        schools: new Set(),
      };
    }
    acc[user.username].reservations.push(...user.reservations);
    acc[user.username].schools.add(user.schoolName);
    return acc;
  }, {});

  return (
    <div>
      <div className="container mt-4">
        <PrintButton
          fileName={`${yearAndMonth} - FECHAMENTO - RELATÓRIO SINTÉTICO`}
        />

        <div className="row">
          <div className="col-3">
            <img src="/logo.png" alt="Logo" width="120" />
          </div>

          <div className="col-9 text-center">
            <h3 className="fw-bold">GAMA - GUARDA MUNICIPAL DE AMERICANA</h3>
            <h3>SISTEMA DE DIÁRIA ADICIONAL POR ESCALA EXTRAORDINÁRIA</h3>
            <h3>FECHAMENTO - RELATÓRIO SINTÉTICO</h3>
          </div>

          <div
            className="col-12 text-end p-2"
            style={{
              display: "inline-block",
              width: "fit-content",
              marginLeft: "auto",
            }}
          >
            <div className="text-center">
              <strong>
                MÊS {monthName} {year}
              </strong>
            </div>
            <div>
              <span>
                DE: {startDate} A {endDate}
              </span>
            </div>
          </div>
        </div>

        {error && <div className="text-danger mt-3">{error}</div>}
        {loading && <p>Carregando...</p>}

        {Object.values(groupedByUser).length > 0 && (
          <div className="mt-4">
            {Object.values(groupedByUser).map((user) => {
              // Split reservations into two equal parts for side-by-side layout
              const half = Math.ceil(user.reservations.length / 2);
              const leftColumn = user.reservations.slice(0, half);
              const rightColumn = user.reservations.slice(half);

              return (
                <div key={user.username} className="mb-5">
                  <h4 style={{ color: "black" }}>
                    {user.name?.toUpperCase()} - MATRÍCULA: {user.username}
                  </h4>
                  <table className="table table-bordered custom-table">
                    <thead>
                      <tr className="school-header bg-secondary text-white">
                        <th colSpan="4" className="text-left school-header">
                          {Array.from(user.schools).join(", ").toUpperCase()}
                        </th>
                      </tr>
                      <tr>
                        <th className="fw-normal">DIA HORA</th>
                        <th className="fw-normal">STATUS</th>
                        <th className="fw-normal">DIA HORA</th>
                        <th className="fw-normal">STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({
                        length: Math.max(leftColumn.length, rightColumn.length),
                      }).map((_, index) => (
                        <tr key={index}>
                          {/* Left Column */}
                          <td>
                            {leftColumn[index]
                              ? formatDateTime(leftColumn[index])
                              : ""}
                          </td>
                          <td>
                            {leftColumn[index] ? leftColumn[index].status : ""}
                          </td>

                          {/* Right Column */}
                          <td>
                            {rightColumn[index]
                              ? formatDateTime(rightColumn[index])
                              : ""}
                          </td>
                          <td>
                            {rightColumn[index]
                              ? rightColumn[index].status
                              : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}

            {/* Total Geral */}
            <div className="mt-4">
              <table className="table table-bordered custom-table">
                <tbody>
                  <tr className="bg-secondary text-white">
                    <td colSpan="3" className="fw-bold school-header">
                      TOTAL GERAL
                    </td>
                    <td className="fw-bold school-header">
                      {Object.values(groupedByUser).reduce(
                        (total, user) => total + user.totalHours,
                        0
                      )}{" "}
                      HORAS
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
